/**
 * The lead search box.
 *
 * One box rather than a row of dropdowns. Whitespace splits the query into
 * tokens and every token must match (AND), so `mine won 3` narrows in the
 * order a rep thinks: my leads, that we won, that scored top marks.
 *
 * A token is interpreted as, in order:
 *   `mine` / `others` / `unassigned`  — ownership
 *   a stage key or stage label        — pipeline position
 *   `0`–`3`, optionally `n/3`         — qualification score
 *   anything else                     — substring over the lead's text fields
 *
 * The stage check runs against the live, admin-editable stage list, so renaming
 * "Proposal" to "Tilbud sendt" makes `tilbud sendt` searchable — as two tokens,
 * which is why a multi-word label is matched on the whole label too.
 */
import type { Lead, PipelineOutcome, PipelineStage } from '@/types/crm'
import { qualificationScore } from '@/types/crm'
import { haystack as joinFields, matchesTokens } from '@/shared/util/tokenSearch'

export interface SearchContext {
  /** Display name of the signed-in rep, for `mine` / `others`. */
  currentUser: string
  stages: (PipelineStage | PipelineOutcome)[]
}

const SCORE_TOKEN = /^([0-3])(\/3)?$/

function haystack(lead: Lead): string {
  return joinFields([
    lead.name,
    lead.owner,
    lead.region,
    lead.country,
    lead.address,
    lead.website,
    lead.instagram,
    lead.facebook,
    lead.teamSize,
  ])
}

function matchesToken(lead: Lead, token: string, ctx: SearchContext): boolean {
  if (token === 'mine') return lead.owner === ctx.currentUser
  if (token === 'others') return lead.owner !== ctx.currentUser && lead.owner !== 'Unassigned'
  if (token === 'unassigned') return lead.owner === 'Unassigned'

  const stage = ctx.stages.find((s) => s.key === token || s.label.toLowerCase() === token)
  if (stage) return lead.stage === stage.key

  const score = SCORE_TOKEN.exec(token)
  if (score) return qualificationScore(lead.qualification) === Number(score[1])

  return haystack(lead).includes(token)
}

export function matchesLeadSearch(lead: Lead, term: string, ctx: SearchContext): boolean {
  return matchesTokens(term, (token) => matchesToken(lead, token, ctx), {
    // A multi-word stage label would otherwise never match, because each word is
    // its own token and neither is a stage on its own.
    isPhrase: (phrase) => ctx.stages.some((s) => s.label.toLowerCase() === phrase),
  })
}
